'use client';

import { useState, useCallback } from 'react';
import type { InteractionStorageAdapter } from '../../model/interaction/adapters';
import { INTERACTION_STORAGE_KEYS } from '../../constants/interaction/constants';

export interface UseOnboardingModalOptions {
  enabled: boolean;
  storage: InteractionStorageAdapter;
}

interface UseOnboardingModalReturn {
  isOnboardingVisible: boolean;
  dismissOnboarding: () => void;
}

/**
 * Controls the one-time onboarding modal visibility.
 * Uses the platform storage adapter so it works on both web (localStorage)
 * and mobile (AsyncStorage) without changes.
 *
 * @param options.enabled - When `false`, skips storage entirely and returns hidden.
 * @param options.storage - The storage adapter to use for persistence.
 */
export function useOnboardingModal({
  enabled,
  storage,
}: UseOnboardingModalOptions): UseOnboardingModalReturn {
  const [dismissed, setDismissed] = useState<boolean>(() => {
    if (!enabled) {
      return true;
    }
    return storage.getItem<boolean>(INTERACTION_STORAGE_KEYS.onboarding, false);
  });

  const dismissOnboarding = useCallback(() => {
    storage.setItem(INTERACTION_STORAGE_KEYS.onboarding, true);
    setDismissed(true);
  }, [storage]);

  return {
    isOnboardingVisible: enabled && !dismissed,
    dismissOnboarding,
  };
}
